import { useMemo } from 'react';

import { monorepoRoutePaths, routePaths as adminRoutePaths } from '@apps/admin/route.paths';
import type { DashboardTelemetry } from '@apps/admin/routes/Dashboard/Dashboard.telemetry';
import { useDashboardTelemetry } from '@apps/admin/routes/Dashboard/Dashboard.telemetry';
import type { GetTranslation, RoutesContextProps } from '@shared/core';
import { useRouterHelper, useTranslation } from '@shared/core';
import { Bell, CustomCard, Email, Emails, Invitation, SaveTheDate, ThankYou, WelcomeSign } from '@withjoy/joykit/icons';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { EventFeature, useProFeature } from '@shared/hooks/useProFeature';

import type { ProductVerticals } from '@shared/core/productVerticals/productVerticals.types';
import type { ProductRow } from '../../PrimaryDashboardContent/PrimaryDashboardContent.types';
import type { ProductRowItemProps } from '../../PrimaryDashboardContent/ProductRowItem';
import type { AdminDashboardV2Translations } from '@locales/generated';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useSmartRouting } from '@shared/hooks/useSmartRouting';

const getCardTilesByCategory = (args: {
  routeHelpers: RoutesContextProps;
  t2: GetTranslation<AdminDashboardV2Translations<string>>;
  telemetry: DashboardTelemetry;
  printThankYouCategoryEnabled?: boolean;
  contextualPath: ReturnType<typeof useSmartRouting> | null;
}): ProductRowItemProps[] => {
  const { routeHelpers, t2, telemetry, printThankYouCategoryEnabled, contextualPath } = args;

  const { invites: invitesTranslations } = t2('sections');
  const invitesStrings = invitesTranslations;

  const dayOfStationeryLink = {
    id: 'stationery-dayOfStationery',
    title: invitesStrings.children.dayOfStationery.title,
    icon: WelcomeSign,
    hideDropdownLabel: true,
    tileBehavior: 'openDropdown' as const,
    subItems: [
      {
        title: invitesStrings.children.dayOfStationery.children.signs.title,
        path: routeHelpers.buildPath('/cards/wedding/sign'),
        pathIsInJoyWeb: true,
        onClick: (categoryArg?: string) => telemetry.openPage({ label: 'dayOfStationery.signs', section: 'Messaging', categoryArg }),
      },
      {
        title: invitesStrings.children.dayOfStationery.children.menus.title,
        path: routeHelpers.buildPath('/cards/wedding/menu'),
        pathIsInJoyWeb: true,
        onClick: (categoryArg?: string) => telemetry.openPage({ label: 'dayOfStationery.menus', section: 'Messaging', categoryArg }),
      },
      {
        title: invitesStrings.children.dayOfStationery.children.napkins.title,
        path: routeHelpers.buildPath('/cards/wedding/napkin'),
        pathIsInJoyWeb: true,
        onClick: (categoryArg?: string) => telemetry.openPage({ label: 'dayOfStationery.napkins', section: 'Messaging', categoryArg }),
      },
    ],
  };

  const links = [
    {
      id: 'stationery-saveTheDates',
      title: invitesStrings.children.saveTheDates.title,
      icon: SaveTheDate,
      hideDropdownLabel: true,
      tileBehavior: 'openDropdown' as const,
      subItems: [
        {
          title: invitesStrings.children.saveTheDates.children.print.title,
          path: routeHelpers.buildPath('/cards/wedding/save-the-date'),
          pathIsInJoyWeb: true,
          onClick: (categoryArg?: string) => telemetry.openPage({ label: 'saveTheDates.paper', section: 'Messaging', categoryArg }),
        },
        {
          // TODO: Perform in-app navigation when we push new ecard editing experience to users
          // https://withjoy.atlassian.net/browse/PRNT-843
          title: invitesStrings.children.saveTheDates.children.digital.title,
          path: routeHelpers.buildPath(monorepoRoutePaths.eCardSaveTheDate),
          onClick: (categoryArg?: string) => telemetry.openPage({ label: 'saveTheDates.digital', section: 'Messaging', categoryArg }),
          telemetryName: monorepoRoutePaths.eCardSaveTheDate,
        },
      ],
    },
    {
      id: 'stationery-invitations',
      title: invitesStrings.children.invitations.title,
      icon: Invitation,
      hideDropdownLabel: true,
      tileBehavior: 'openDropdown' as const,
      subItems: [
        {
          title: invitesStrings.children.invitations.children.print.title,
          path: routeHelpers.buildPath('/cards/wedding/invitation'),
          pathIsInJoyWeb: true,
          onClick: (categoryArg?: string) => telemetry.openPage({ label: 'invitations.paper', section: 'Messaging', categoryArg }),
        },
        {
          // TODO: Perform in-app navigation when we push new ecard editing experience to users
          // https://withjoy.atlassian.net/browse/PRNT-843
          title: invitesStrings.children.invitations.children.digital.title,
          path: routeHelpers.buildPath(monorepoRoutePaths.eCardInvitations),
          onClick: (categoryArg?: string) => telemetry.openPage({ label: 'invitations.digital', section: 'Messaging', categoryArg }),
          telemetryName: monorepoRoutePaths.eCardInvitations,
        },
      ],
    },
    dayOfStationeryLink,
    ...(printThankYouCategoryEnabled
      ? [
          {
            id: 'stationery-thankYou',
            title: invitesStrings.children.thankYou.title,
            icon: ThankYou,
            hideDropdownLabel: true,
            path: routeHelpers.buildPath('/cards/wedding/thank-you'),
            pathIsInJoyWeb: true,
            onClick: (categoryArg?: string) => telemetry.openPage({ label: 'invitations.thankYou', section: 'Messaging', categoryArg }),
          },
        ]
      : []),
  ];

  const contextualLinks = [
    {
      id: 'stationery-saveTheDates-contextual',
      title: invitesStrings.children.saveTheDates.title,
      icon: SaveTheDate,
      hideDropdownLabel: true,
      path: contextualPath?.saveTheDate ?? undefined,
      pathIsInJoyWeb: true,
      onClick: (categoryArg?: string) => telemetry.openPage({ label: 'saveTheDates.paper', section: 'Messaging', categoryArg }),
    },
    {
      id: 'stationery-invitations-contextual',
      title: invitesStrings.children.invitations.title,
      icon: Invitation,
      hideDropdownLabel: true,
      path: contextualPath?.invitation ?? undefined,
      pathIsInJoyWeb: true,
      onClick: (categoryArg?: string) => telemetry.openPage({ label: 'invitations.paper', section: 'Messaging', categoryArg }),
    },
    dayOfStationeryLink,
    ...(printThankYouCategoryEnabled
      ? [
          {
            id: 'stationery-thankYou-contextual',
            title: invitesStrings.children.thankYou.title,
            icon: ThankYou,
            hideDropdownLabel: true,
            path: contextualPath?.thankYou ?? undefined,
            pathIsInJoyWeb: true,
            onClick: (categoryArg?: string) => telemetry.openPage({ label: 'invitations.thankYou', section: 'Messaging', categoryArg }),
          },
        ]
      : []),
  ];

  return [...(contextualPath ? contextualLinks : links)];
};

export const useInvitationsProductRow = (args: {
  productVerticals: ProductVerticals;
  printThankYouCategoryEnabled: boolean;
  hotelBlockReminderEnabled: boolean;
  enableRsvpReminderMessage: boolean;
  enableContactCollectionMessage: boolean;
  eventId?: string;
}): ProductRow => {
  const { productVerticals, printThankYouCategoryEnabled, hotelBlockReminderEnabled, enableRsvpReminderMessage, enableContactCollectionMessage } = args;
  const routeHelpers = useRouterHelper();
  const telemetry = useDashboardTelemetry();
  const { t2, tq } = useTranslation('adminDashboardV2');
  const { eventInfo } = useEventInfo();

  const useContextualLinks = eventInfo?.featureFlags?.includes('useModernEcards');
  const smartRoutingResult = useSmartRouting({ routeHelpers, skip: !useContextualLinks });
  const contextualPath = useContextualLinks && !smartRoutingResult.isLoading ? smartRoutingResult : null;

  const [isNewCashFundsEnabled] = useIsFeatureEnabled('newCashFundsEnabled');
  const isCashFundMessagingEnabled = useProFeature(EventFeature.cashFundMessaging);

  const invitationsProductRow = useMemo<ProductRow>(() => {
    const { invites: invitesTranslations } = t2('sections');
    const invitesStrings = invitesTranslations;

    const emailTile: ProductRowItemProps =
      hotelBlockReminderEnabled || enableRsvpReminderMessage
        ? {
            id: 'stationery-messaging',
            title: invitesStrings.children.messaging.title,
            icon: Email,
            hideDropdownLabel: true,
            tileBehavior: 'openDropdown',
            subItems: [
              {
                title: invitesStrings.children.messaging.children.tracking.title,
                path: routeHelpers.buildPath('/messaging'),
                onClick: (categoryArg?: string) => telemetry.openPage({ label: 'tracking', section: 'Messaging', categoryArg }),
                telemetryName: '/messaging',
              },
              {
                title: invitesStrings.children.messaging.children.reminder.title,
                path: routeHelpers.buildPath(monorepoRoutePaths.emailCreate + '?draftType=reminder&fromDashboard=true'),
                onClick: (categoryArg?: string) => telemetry.openPage({ label: 'reminderemail', section: 'Messaging', categoryArg }),
                telemetryName: monorepoRoutePaths.emailCreate + '/reminderemail',
              },
              ...(hotelBlockReminderEnabled
                ? [
                    {
                      title: invitesStrings.children.messaging.children.hotel.title,
                      path: routeHelpers.buildPath('/hotel-reminder'),
                      onClick: (categoryArg?: string) => telemetry.openPage({ label: 'hotelreminder', section: 'Messaging', categoryArg }),
                      telemetryName: monorepoRoutePaths.emailCreate + '/hotelreminder',
                    },
                  ]
                : []),
              ...(enableRsvpReminderMessage
                ? [
                    {
                      title: invitesStrings.children.messaging.children.rsvp.title,
                      path: routeHelpers.buildPath('/send-rsvp-reminder'),
                      onClick: (categoryArg?: string) => telemetry.openPage({ label: 'rsvpreminder', section: 'Messaging', categoryArg }),
                      telemetryName: monorepoRoutePaths.emailCreate + '/rsvpreminder',
                    },
                  ]
                : []),
              ...(enableContactCollectionMessage
                ? [
                    {
                      title: tq`Collect Contacts`,
                      path: routeHelpers.buildPath('/collect-contacts/create'),
                      onClick: (categoryArg?: string) => telemetry.openPage({ label: 'collectcontacts', section: 'Messaging', categoryArg }),
                      telemetryName: '/collect-contacts/create',
                    },
                  ]
                : []),
              ...(isNewCashFundsEnabled
                ? [
                    {
                      title: tq`Cash Funds`,
                      hideDropdownLabel: true,
                      path: routeHelpers.buildPath(adminRoutePaths.cashfunds.path),
                      pathIsInJoyWeb: true,
                      onClick: (categoryArg?: string) => telemetry.openPage({ label: 'cashFunds', section: 'Messaging', categoryArg }),
                      telemetryName: adminRoutePaths.cashfunds.path,
                      hasNewPill: true,
                    },
                  ]
                : []),
              ...(isCashFundMessagingEnabled
                ? [
                    {
                      title: tq`Request Funds`,
                      path: routeHelpers.buildPath('/message/general/create?draftType=cashFund&fromDashboard=true'),
                      onClick: (categoryArg?: string) => telemetry.openPage({ label: 'requestfunds', section: 'Messaging', categoryArg }),
                      telemetryName: '/message/general/create/requestfunds',
                    },
                  ]
                : []),
              {
                title: invitesStrings.children.messaging.children.general.title,
                path: routeHelpers.buildPath(monorepoRoutePaths.emailCreate + '?draftType=generic&fromDashboard=true'),
                onClick: (categoryArg?: string) => telemetry.openPage({ label: 'generalmessage', section: 'Messaging', categoryArg }),
                telemetryName: monorepoRoutePaths.emailCreate + '/generalmessage',
              },
              {
                title: invitesStrings.children.customCards.title,
                path: routeHelpers.buildPath(monorepoRoutePaths.eCardCustom),
                onClick: (categoryArg?: string) => telemetry.openPage({ label: 'customCards', section: 'Messaging', categoryArg }),
                telemetryName: monorepoRoutePaths.eCardCustom,
              },
            ],
          }
        : {
            id: 'stationery-emails',
            title: invitesStrings.children.emails.title,
            icon: Emails,
            tileBehavior: 'openDropdown',
            subItems: [
              {
                title: invitesStrings.children.emails.children.reminder.title,
                icon: Bell,
                path: routeHelpers.buildPath(monorepoRoutePaths.emailCreate + '?draftType=reminder&fromDashboard=true'),
                onClick: (categoryArg?: string) => telemetry.openPage({ label: 'reminderemail', section: 'Messaging', categoryArg }),
                telemetryName: monorepoRoutePaths.emailCreate + '/reminderemail',
              },
              {
                title: invitesStrings.children.emails.children.general.title,
                icon: Email,
                path: routeHelpers.buildPath(monorepoRoutePaths.emailCreate + '?draftType=generic&fromDashboard=true'),
                onClick: (categoryArg?: string) => telemetry.openPage({ label: 'generalmessage', section: 'Messaging', categoryArg }),
                telemetryName: monorepoRoutePaths.emailCreate + '/generalmessage',
              },
              {
                title: invitesStrings.children.customCards.title,
                icon: CustomCard,
                path: routeHelpers.buildPath(monorepoRoutePaths.eCardCustom),
                onClick: (categoryArg?: string) => telemetry.openPage({ label: 'customCards', section: 'Messaging', categoryArg }),
                telemetryName: monorepoRoutePaths.eCardCustom,
              },
            ],
          };

    return {
      title: invitesStrings.title,
      isEnabled: !productVerticals.optOutOfInvites,
      children: [
        ...getCardTilesByCategory({
          routeHelpers,
          t2,
          telemetry,
          printThankYouCategoryEnabled,
          contextualPath,
        }),
        emailTile,
      ],
    };
  }, [
    t2,
    tq,
    hotelBlockReminderEnabled,
    enableRsvpReminderMessage,
    enableContactCollectionMessage,
    routeHelpers,
    productVerticals.optOutOfInvites,
    telemetry,
    printThankYouCategoryEnabled,
    contextualPath,
    isNewCashFundsEnabled,
    isCashFundMessagingEnabled,
  ]);

  return invitationsProductRow;
};
